class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleEscape = this.handleEscape.bind(this);
    this.handleCartRefresh = this.handleCartRefresh.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleOverlayClick = this.close.bind(this);

    this.addEventListener('keyup', this.handleEscape);

    this.bindOverlay();
    this.setHeaderCartIconAccessibility();

    document.addEventListener('cart:refresh', this.handleCartRefresh);
    document.addEventListener('cart:updated', this.handleCartUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('keyup', this.handleEscape);
    document.removeEventListener('cart:refresh', this.handleCartRefresh);
    document.removeEventListener('cart:updated', this.handleCartUpdate);
  }

  handleEscape(evt) {
    if (evt.code === 'Escape') this.close();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;

    const newOverlay = overlay.cloneNode(true);
    overlay.parentNode.replaceChild(newOverlay, overlay);
    newOverlay.addEventListener('click', this.handleOverlayClick);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', this.classList.contains('active') ? 'true' : 'false');

    const newCartLink = cartLink.cloneNode(true);
    cartLink.parentNode.replaceChild(newCartLink, cartLink);

    newCartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(newCartLink);
    });

    newCartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
        event.preventDefault();
        this.open(newCartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (cartLink) cartLink.setAttribute('aria-expanded', 'true');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) cartLink.setAttribute('aria-expanded', 'false');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections[section.id]) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (parsedState.sections['cart-icon-bubble']) {
      this.setHeaderCartIconAccessibility();
    }

    this.bindOverlay();

    document.dispatchEvent(new CustomEvent('cart:drawer:rendered'));

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  handleCartRefresh() {
    this.refreshCart(true);
  }

  handleCartUpdate(event) {
    if (event?.detail?.cart?.item_count !== undefined) {
      this.updateCartCount(event.detail.cart.item_count);
    }
    this.refreshCart(false);
  }

  refreshCart(openDrawer = false) {
    const cartUrl = `${window.location.origin}/?sections=cart-drawer,cart-icon-bubble`;

    fetch(cartUrl, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((sections) => {
        if (sections['cart-drawer']) {
          const parsedDrawer = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          const sourceDrawerInner =
            parsedDrawer.querySelector('#CartDrawer .drawer__inner') ||
            parsedDrawer.querySelector('.drawer__inner');

          const destinationDrawerInner = this.querySelector('.drawer__inner');

          if (sourceDrawerInner && destinationDrawerInner) {
            destinationDrawerInner.innerHTML = sourceDrawerInner.innerHTML;

            if (sourceDrawerInner.querySelector('.cart-item')) {
              destinationDrawerInner.classList.remove('is-empty');
            } else {
              destinationDrawerInner.classList.add('is-empty');
            }
          }
        }

        if (sections['cart-icon-bubble']) {
          const parsedBubble = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          const sourceBubble =
            parsedBubble.querySelector('#cart-icon-bubble') ||
            parsedBubble.querySelector('.shopify-section');

          const destinationBubble = document.querySelector('#cart-icon-bubble');

          if (sourceBubble && destinationBubble) {
            destinationBubble.innerHTML = sourceBubble.innerHTML;
            destinationBubble.style.display = '';
            destinationBubble.classList.remove('hidden');
          }
        }

        this.setHeaderCartIconAccessibility();
        this.bindOverlay();

        document.dispatchEvent(new CustomEvent('cart:drawer:rendered'));

        if (openDrawer) {
          this.open();
        }
      })
      .catch((e) => {
        console.error('Error refreshing cart:', e);
      });
  }

  updateCartCount(count) {
    const cartCountElements = document.querySelectorAll(
      '.cart-count, .cart-item-count, .cart-count-bubble'
    );

    cartCountElements.forEach((element) => {
      element.textContent = count;

      if (count > 0) {
        const cartIconBubble = element.closest('#cart-icon-bubble');
        if (cartIconBubble) {
          cartIconBubble.style.display = '';
          cartIconBubble.classList.remove('hidden');
        }
      }
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);